import { nanoid } from "nanoid";
import { absolutePosition } from "./auto-fuse";
import { shouldPruneWaypoints } from "./cable-routing";
import { getDefinition } from "./catalog";
import { busbarStudList, effectiveTerminals } from "./effective";
import { isJunction } from "./node-kind";
import { SCHEMA_VERSION } from "./schema/version";
import type { Selection } from "./store";
import { terminalAbsPosition } from "./terminal-snap";
import type { Cable, System, SystemNode } from "./types";
import { deepClone } from "./utils";

export const emptySystem = (name = "Untitled System"): System => ({
  name,
  nodes: [],
  cables: [],
  zoneDistances: {},
  version: SCHEMA_VERSION,
});

export function endpointPos(
  nodeId: string,
  terminalId: string,
  nodes: SystemNode[],
): { x: number; y: number } | null {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) return null;
  if (node.kind === "zone") return null;
  const def = getDefinition(node.definitionId, node.customDefinition);
  if (isJunction(node, def)) {
    const abs = absolutePosition(node, nodes);
    return { x: abs.x + 7, y: abs.y + 7 };
  }
  if (!def) return null;
  const term = effectiveTerminals(def, node).find((t) => t.id === terminalId);
  if (!term) return null;
  return terminalAbsPosition(node, def, term, nodes);
}

export function cloneSystem(s: System): System {
  return deepClone(s);
}

export function filterSelectionToAlive(
  selection: Selection,
  nodes: SystemNode[],
  cables: Cable[],
): Selection {
  const nodeIdsAlive = new Set(nodes.map((n) => n.id));
  const cableIdsAlive = new Set(cables.map((c) => c.id));
  return {
    nodeIds: selection.nodeIds.filter((id) => nodeIdsAlive.has(id)),
    cableIds: selection.cableIds.filter((id) => cableIdsAlive.has(id)),
  };
}

/** Only a two-cable junction splices into one bypassing cable; any other count is
 *  left to the caller's normal removal path. */
export function mergeCablesAcrossDeletedJunctions(
  nodes: SystemNode[],
  cables: Cable[],
  removedNodeIds: Set<string>,
): { cables: Cable[]; consumedCableIds: Set<string> } {
  const consumedCableIds = new Set<string>();
  const replacements: Cable[] = [];
  for (const node of nodes) {
    if (node.kind !== "component" || !isJunction(node)) continue;
    if (!removedNodeIds.has(node.id)) continue;
    const touching = cables.filter(
      (c) => c.fromNodeId === node.id || c.toNodeId === node.id,
    );
    if (touching.length !== 2) continue;
    const [a, b] = touching;
    if (consumedCableIds.has(a.id) || consumedCableIds.has(b.id)) continue;
    const aFar = a.fromNodeId === node.id
      ? { nodeId: a.toNodeId, terminalId: a.toTerminalId }
      : { nodeId: a.fromNodeId, terminalId: a.fromTerminalId };
    const bFar = b.fromNodeId === node.id
      ? { nodeId: b.toNodeId, terminalId: b.toTerminalId }
      : { nodeId: b.fromNodeId, terminalId: b.fromTerminalId };
    if (removedNodeIds.has(aFar.nodeId) || removedNodeIds.has(bFar.nodeId)) continue;
    consumedCableIds.add(a.id);
    consumedCableIds.add(b.id);
    const fused = [a, b].filter(c => c.fuseAmpOverride != null);
    const fuseSource = fused[0];
    // Preserve the existing first-declaration rating choice. Only carry Auto
    // across multiple declarations when they agree; a manual, legacy or
    // conflicting pick must never become refreshable merely through layout.
    const matchingAuto = fused.every(c => c.fuseAmpAuto === true &&
      c.fuseAmpOverride === fuseSource?.fuseAmpOverride &&
      c.fuseTypeOverride === fuseSource?.fuseTypeOverride);
    const fuseAmpAuto = fuseSource?.fuseAmpAuto === true && !matchingAuto
      ? undefined : fuseSource?.fuseAmpAuto;
    replacements.push({
      id: nanoid(8),
      fromNodeId: aFar.nodeId,
      fromTerminalId: aFar.terminalId,
      toNodeId: bFar.nodeId,
      toTerminalId: bFar.terminalId,
      lengthM: a.lengthM + b.lengthM,
      wireSizeMm2Override: a.wireSizeMm2Override ?? b.wireSizeMm2Override ?? null,
      ...((a.ampacityDerating != null || b.ampacityDerating != null) ? {
        ampacityDerating: Math.min(a.ampacityDerating ?? 1, b.ampacityDerating ?? 1),
        vdropClass: a.vdropClass === "critical" || b.vdropClass === "critical" ? "critical" as const : a.vdropClass ?? b.vdropClass,
      } : {}),
      fuseAmpOverride: a.fuseAmpOverride ?? b.fuseAmpOverride ?? null,
      ...(fuseSource?.fuseTypeOverride != null ? { fuseTypeOverride: fuseSource.fuseTypeOverride } : {}),
      ...(fuseAmpAuto != null ? { fuseAmpAuto } : {}),
      notes: a.notes ?? b.notes,
    });
  }
  if (consumedCableIds.size === 0) return { cables, consumedCableIds };
  return { cables: [...cables, ...replacements], consumedCableIds };
}

/** Drop stored waypoints on cables touching `nodeId` when the saved polyline is
 *  now worse than a fresh route. Run after the node moved, changed parent, or had
 *  a terminal repositioned — each strands waypoints on a stale anchor. */
export function pruneStaleWaypointsForNode(
  nodes: SystemNode[],
  cables: Cable[],
  nodeId: string,
): Cable[] {
  return cables.map((c) => {
    if (!c.waypoints || c.waypoints.length === 0) return c;
    if (c.fromNodeId !== nodeId && c.toNodeId !== nodeId) return c;
    const src = endpointPos(c.fromNodeId, c.fromTerminalId, nodes);
    const tgt = endpointPos(c.toNodeId, c.toTerminalId, nodes);
    if (!src || !tgt) return c;
    if (!shouldPruneWaypoints(src, tgt, c.waypoints)) return c;
    const { waypoints: _w, ...rest } = c;
    return rest as Cable;
  });
}

/** A junction with no cables goes, and one with a single dangling cable goes with
 *  it — unless it is `provisional`, the outlined-circle state an empty-pane drop
 *  creates and which must survive. */
export function pruneOrphanedJunctions(
  nodes: SystemNode[],
  cables: Cable[],
): { nodes: SystemNode[]; cables: Cable[] } {
  let curNodes = nodes;
  let curCables = cables;
  for (;;) {
    const nodesToRemove = new Set<string>();
    const cablesToRemove = new Set<string>();
    for (const n of curNodes) {
      if (n.kind !== "component" || !isJunction(n)) continue;
      const touching = curCables.filter(
        (c) => c.fromNodeId === n.id || c.toNodeId === n.id,
      );
      if (touching.length === 0) {
        nodesToRemove.add(n.id);
      } else if (touching.length === 1 && !n.provisional) {
        nodesToRemove.add(n.id);
        cablesToRemove.add(touching[0].id);
      }
    }
    if (nodesToRemove.size === 0 && cablesToRemove.size === 0) break;
    curNodes = curNodes.filter((n) => !nodesToRemove.has(n.id));
    curCables = curCables.filter((c) => !cablesToRemove.has(c.id));
  }
  return { nodes: curNodes, cables: curCables };
}

/** Shrink each busbar's `studCount` to its cable count (min 1). A cable on a stud
 *  id that no longer exists is remapped to a free one, and overrides for vanished
 *  ids are dropped so a later grow can't resurrect a stale offset. */
export function shrinkBusbarsToCableCount(
  nodes: SystemNode[],
  cables: Cable[],
  candidateNodeIds: Iterable<string>,
): { nodes: SystemNode[]; cables: Cable[] } {
  const candidates = new Set(candidateNodeIds);
  if (candidates.size === 0) return { nodes, cables };

  let outNodes = nodes;
  let outCables = cables;

  for (const nodeId of candidates) {
    const idx = outNodes.findIndex((n) => n.id === nodeId);
    if (idx < 0) continue;
    const node = outNodes[idx];
    if (node.kind !== "component") continue;
    const def = getDefinition(node.definitionId, node.customDefinition);
    if (!def || def.renderVariant !== "busbar") continue;

    const studAxis: "left" | "right" | "top" | "bottom" =
      def.height >= def.width ? "right" : "bottom";
    const catalogStuds = def.terminals.filter((t) => t.side === studAxis).length;
    // Studs only — the input feed is on the opposite face, and counting it
    // would leave a phantom empty stud.
    const studTermIds = new Set(
      busbarStudList(def, node.studCount).map((t) => t.id),
    );
    const cableCount = outCables.reduce((acc, c) => {
      if (c.fromNodeId === nodeId && studTermIds.has(c.fromTerminalId)) return acc + 1;
      if (c.toNodeId === nodeId && studTermIds.has(c.toTerminalId)) return acc + 1;
      return acc;
    }, 0);
    const target = Math.max(1, cableCount);
    const current = node.studCount ?? catalogStuds;
    if (target >= current) continue;

    const oldStudIds = new Set(busbarStudList(def, current).map((t) => t.id));
    const newStuds = busbarStudList(def, target);
    const newStudIds = new Set(newStuds.map((t) => t.id));

    const reserved = new Set<string>();
    for (const c of outCables) {
      if (c.fromNodeId === nodeId && newStudIds.has(c.fromTerminalId)) {
        reserved.add(c.fromTerminalId);
      }
      if (c.toNodeId === nodeId && newStudIds.has(c.toTerminalId)) {
        reserved.add(c.toTerminalId);
      }
    }
    const available = newStuds.map((t) => t.id).filter((id) => !reserved.has(id));

    outCables = outCables.map((c) => {
      let next = c;
      if (
        c.fromNodeId === nodeId &&
        oldStudIds.has(c.fromTerminalId) &&
        !newStudIds.has(c.fromTerminalId)
      ) {
        const newId = available.shift();
        if (newId) next = { ...next, fromTerminalId: newId };
      }
      if (
        c.toNodeId === nodeId &&
        oldStudIds.has(c.toTerminalId) &&
        !newStudIds.has(c.toTerminalId)
      ) {
        const newId = available.shift();
        if (newId) next = { ...next, toTerminalId: newId };
      }
      return next;
    });

    let nextOverrides = node.terminalOverrides;
    if (nextOverrides) {
      const filtered: typeof nextOverrides = {};
      let dropped = false;
      for (const [termId, ov] of Object.entries(nextOverrides)) {
        if (oldStudIds.has(termId) && !newStudIds.has(termId)) {
          dropped = true;
          continue;
        }
        filtered[termId] = ov;
      }
      if (dropped) {
        nextOverrides =
          Object.keys(filtered).length === 0 ? undefined : filtered;
      }
    }

    const updated: SystemNode = {
      ...node,
      studCount: target,
      ...(nextOverrides === node.terminalOverrides
        ? {}
        : { terminalOverrides: nextOverrides }),
    };
    outNodes = outNodes.map((n) => (n.id === nodeId ? updated : n));
  }

  return { nodes: outNodes, cables: outCables };
}

export function sameIds(a: string[], b: string[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** Expand a seed set of cable ids to the minimal group re-tidied together: cables
 *  sharing a `(nodeId, terminalId)` splay as one fan-out, and cables sharing a
 *  junction *and* a cardinal side fan into one anchor. Widening it further throws
 *  away the user's hand-shaped routing. */
export function expandCableTidyScope(
  nodes: SystemNode[],
  cables: Cable[],
  seedCableIds: Set<string>,
): Set<string> {
  const seedCables = cables.filter((c) => seedCableIds.has(c.id));
  if (seedCables.length === 0) return new Set();

  const junctionIds = new Set<string>();
  for (const n of nodes) {
    if (n.kind !== "component") continue;
    const def = getDefinition(n.definitionId, n.customDefinition);
    if (isJunction(n, def)) junctionIds.add(n.id);
  }

  // Must match `pickJunctionSide` in `canvas.tsx` — the handle React Flow assigns.
  const junctionSideToward = (
    junctionId: string,
    farPoint: { x: number; y: number },
  ): "top" | "right" | "bottom" | "left" | null => {
    const jp = endpointPos(junctionId, "", nodes);
    if (!jp) return null;
    const dx = farPoint.x - jp.x;
    const dy = farPoint.y - jp.y;
    if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? "right" : "left";
    return dy >= 0 ? "bottom" : "top";
  };
  const sideKey = (junctionId: string, side: string) => `${junctionId}/${side}`;
  const junctionSidesTouched = new Set<string>();
  for (const c of seedCables) {
    if (junctionIds.has(c.fromNodeId)) {
      const far = endpointPos(c.toNodeId, c.toTerminalId, nodes);
      const side = far ? junctionSideToward(c.fromNodeId, far) : null;
      if (side) junctionSidesTouched.add(sideKey(c.fromNodeId, side));
    }
    if (junctionIds.has(c.toNodeId)) {
      const far = endpointPos(c.fromNodeId, c.fromTerminalId, nodes);
      const side = far ? junctionSideToward(c.toNodeId, far) : null;
      if (side) junctionSidesTouched.add(sideKey(c.toNodeId, side));
    }
  }

  const expanded = new Set(seedCableIds);
  if (junctionSidesTouched.size > 0) {
    for (const c of cables) {
      if (expanded.has(c.id)) continue;
      if (junctionIds.has(c.fromNodeId)) {
        const far = endpointPos(c.toNodeId, c.toTerminalId, nodes);
        const side = far ? junctionSideToward(c.fromNodeId, far) : null;
        if (side && junctionSidesTouched.has(sideKey(c.fromNodeId, side))) {
          expanded.add(c.id);
          continue;
        }
      }
      if (junctionIds.has(c.toNodeId)) {
        const far = endpointPos(c.fromNodeId, c.fromTerminalId, nodes);
        const side = far ? junctionSideToward(c.toNodeId, far) : null;
        if (side && junctionSidesTouched.has(sideKey(c.toNodeId, side))) {
          expanded.add(c.id);
        }
      }
    }
  }

  const termKey = (nodeId: string, terminalId: string) => `${nodeId}/${terminalId}`;
  const terminalsTouched = new Set<string>();
  for (const c of seedCables) {
    if (!junctionIds.has(c.fromNodeId)) {
      terminalsTouched.add(termKey(c.fromNodeId, c.fromTerminalId));
    }
    if (!junctionIds.has(c.toNodeId)) {
      terminalsTouched.add(termKey(c.toNodeId, c.toTerminalId));
    }
  }
  if (terminalsTouched.size > 0) {
    for (const c of cables) {
      if (expanded.has(c.id)) continue;
      if (
        terminalsTouched.has(termKey(c.fromNodeId, c.fromTerminalId)) ||
        terminalsTouched.has(termKey(c.toNodeId, c.toTerminalId))
      ) {
        expanded.add(c.id);
      }
    }
  }
  return expanded;
}
